import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Calendar,
  BookOpen,
  Clock,
  Trophy,
  Award,
  AlertTriangle,
  CreditCard,
  XCircle,
  Video,
  Users,
  HelpCircle,
  Brain
} from 'lucide-react';
import { getCurrentLevel, getNextLevel, getProgressToNextLevel } from '../utils/levels';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import BackButton from '../components/BackButton';
import { api } from '../services/api';
import type { QuizStats } from '../types';
import { LEVELS } from '../utils/levels';

export default function Profile() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user, signOut } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [showLevelHelp, setShowLevelHelp] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [moduleProgress, setModuleProgress] = useState<ModuleProgress[]>([]);
  const [globalProgress, setGlobalProgress] = useState(0);
  const [quizStats, setQuizStats] = useState<QuizStats | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [canTakeQuiz, setCanTakeQuiz] = useState<{ can_take: boolean; reason?: string } | null>(null);
  const navigate = useNavigate();

  const isUnsubscribed = user?.unsubscribed_at ? true : false;

  const canBookGroupCalls = user?.subscription?.includes('DETERMINED_');
  const canBookIndividualCalls = user?.subscription === 'DETERMINED_ANNUALLY';

  const getGroupCallLink = () => {
    if (user?.subscription?.includes('DETERMINED_')) {
      return 'https://calendly.com/loustaletformation/formation-de-groupe-abonne';
    }
    return 'https://calendly.com/loustaletformation/formation-de-groupe-abonne-clone';
  };

  const getIndividualCallLink = () => {
    if (user?.subscription === 'DETERMINED_ANNUALLY') {
      return 'https://calendly.com/loustaletformation/30min';
    }
    return 'https://calendly.com/loustaletformation/rendez-vous-individuel-abonne-clone';
  };

  useEffect(() => {
    if (!user) return;

    const hasSubscription = hasActiveSubscription();
    if (hasSubscription) {
      Promise.all([
        api.getUserProgress(),
        api.getQuizStats(),
        user?.subscription?.includes('SHY') ? api.canTakeQuiz() : Promise.resolve({ can_take: true })
      ]).then(([progressData, statsData, quizPermission]) => {
        let totalProgress = 0;
        console.log('progressData', progressData);
        if (Array.isArray(progressData.modules_progress)) {
          const arr = progressData.modules_progress;
          totalProgress = arr.length
            ? Math.round(
                arr.reduce((acc, cur) => acc + (cur.progress ?? 0), 0) / arr.length
              )
            : 0;
          setModuleProgress(arr);
        } else if (typeof progressData.modules_progress === 'number') {
          totalProgress = progressData.modules_progress;
        }
        console.log('Total progress:', totalProgress);
        setGlobalProgress(totalProgress);
        setQuizStats(statsData);
        setCanTakeQuiz(quizPermission);
        console.log('Quiz stats:', statsData);
        console.log('Can take quiz:', quizPermission);
      }).catch(error => {
        console.error('Error fetching profile data:', error);
      });
    }
  }, [user?.id, user?.subscribed_until]); // Only depend on user ID and subscription end date

  const lastQuizScore = quizStats?.score_history && quizStats.score_history.length > 0
    ? quizStats.score_history[quizStats.score_history.length - 1].score
    : 0;
  const bestQuizScore = quizStats?.score_history && quizStats.score_history.length > 0
    ? Math.max(...quizStats.score_history.map(entry => entry.score))
    : 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getSubscriptionLabel = (type: string): string => {
    switch (type) {
      case 'DETERMINED_MONTHLY':
        return 'Déterminé (mensuel)';
      case 'DETERMINED_ANNUALLY':
        return 'Déterminé (annuel)';
      case 'MOTIVATED_MONTHLY':
        return 'Motivé (mensuel)';
      case 'MOTIVATED_ANNUALLY':
        return 'Motivé (annuel)';
      case 'SHY_MONTHLY':
        return 'Timide (mensuel)';
      case 'SHY_ANNUALLY':
        return 'Timide (annuel)';
      default:
        return type;
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.deleteAccount();
      signOut();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Une erreur est survenue lors de la suppression du compte. Veuillez réessayer.');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await api.cancelSubscription();
      // Refresh user data to update subscription status
      window.location.reload();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Une erreur est survenue lors de l\'annulation de l\'abonnement. Veuillez réessayer.');
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>

        {/* Header Section */}
        <div className={`bg-white rounded-xl shadow-sm p-6 border-t-4 border-${theme.primary} mb-8`}>
          <div className="flex items-center justify-between mb-4">
            {!imageError ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-12 h-12 rounded-full object-cover border border-gray-200"
                onError={handleImageError}
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-lg">
                😊
              </div>
            )}
            {user && (
              <div className="flex items-center">
                <div className="text-right mr-2">
                  <div className="flex items-center">
                    <p className="font-semibold">{user.level}</p>
                    <button
                      onClick={() => setShowLevelHelp(true)}
                      className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">{user.experience} XP</p>
                </div>
                <img
                  src={getCurrentLevel(user.experience).icon}
                  alt={user.level}
                  className="w-8 h-8 object-contain"
                />
              </div>
            )}
          </div>
          <h3 className="text-xl font-semibold mb-1">{user.name}</h3>
          <p className="text-gray-600 mb-4">
            Mon objectif : {user.objective || 'Non défini'}
            {user && getNextLevel(user.experience) && (
              <span className="block mt-2 text-sm flex items-center">
                Prochain niveau : {getNextLevel(user.experience)?.name}
                <img
                  src={getNextLevel(user.experience)?.icon}
                  alt={getNextLevel(user.experience)?.name}
                  className="w-4 h-4 object-contain ml-1 mr-1"
                />
                ({getProgressToNextLevel(user.experience)}%)
              </span>
            )}
          </p>
          <div className="space-y-3 text-gray-600">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <Mail className="h-6 w-6 mr-3" />
              {user.email}
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <Calendar className="h-6 w-6 mr-3" />
              Membre depuis le {formatDate(new Date().toISOString())}
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <CreditCard className="h-6 w-6 mr-3" />
              <div>
                {user.subscription && user.subscribed_at && user.subscribed_until ? (
                  <div>
                    <div className="mb-2">
                      <p className="font-medium">Abonnement {getSubscriptionLabel(user.subscription)}</p>
                      {user.subscribed_at && user.subscribed_until && (
                        <p className="text-sm text-gray-500">
                          Du {formatDate(user.subscribed_at)} au {formatDate(user.subscribed_until)}
                        </p>
                      )}
                      {isUnsubscribed && (
                        <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-orange-700 font-medium text-sm">
                            ⚠️ Abonnement annulé - Ne sera pas renouvelé
                          </p>
                          <p className="text-orange-600 text-xs mt-1">
                            Vous conservez l'accès jusqu'au {formatDate(user.subscribed_until)}
                          </p>
                        </div>
                      )}
                    </div>
                    {!isUnsubscribed && (
                      <div className="flex items-center space-x-4 mt-4">
                        <button
                          onClick={() => navigate('/tarifs?upgrade=true')}
                          className={`px-4 py-2 bg-${theme.primary} text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium`}
                        >
                          Changer de formule
                        </button>
                        <button
                          onClick={() => setShowUnsubscribeConfirm(true)}
                          className="px-4 py-2 border border-red-500 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                        >
                          Se désabonner
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Aucun abonnement actif.{" "}
                    {!isUnsubscribed && (
                      <button
                        onClick={() => navigate('/tarifs')}
                        className={`text-${theme.primary} hover:underline`}
                      >
                        Voir les formules
                      </button>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Video Call Booking Section */}
        {hasActiveSubscription() && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">Réserver une visioconférence</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`bg-${theme.secondary} rounded-lg p-6`}>
                <div className="flex items-center mb-4">
                  <Video className={`h-8 w-8 text-${theme.text} mr-3`} />
                  <div>
                    <h3 className="text-lg font-semibold">Visio individuelle</h3>
                    <p className="text-sm text-gray-500">
                      {canBookIndividualCalls ? 'Gratuit' : '50€ par session'}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  Bénéficiez d'un accompagnement personnalisé en tête-à-tête.
                </p>
                <a
                  href={getIndividualCallLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center px-6 py-3 rounded-lg w-full justify-center border-2 border-${theme.primary} text-${theme.text} hover:bg-${theme.secondary} transition-colors`}
                >
                  <Video className="h-5 w-5 mr-2" />
                  Réserver une session individuelle
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Progress Overview */}
        {hasActiveSubscription() && (
          <>
            {/* SHY Subscription Daily Limit Info */}
            {user?.subscription?.includes('SHY') && quizStats && (
              <div className={`mb-8 p-6 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
                <h3 className="font-semibold mb-2 flex items-center">
                  <Brain className={`h-5 w-5 text-${theme.text} mr-2`} />
                  Limite quotidienne - Formule Timide
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">
                    Quiz effectués aujourd'hui : <strong>{canTakeQuiz && !canTakeQuiz.can_take ? '1' : '0'}/1</strong>
                  </p>
                  {canTakeQuiz && !canTakeQuiz.can_take ? (
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      Limite atteinte
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      Disponible
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Passez à une formule supérieure pour des quiz illimités.{' '}
                  <button
                    onClick={() => navigate('/tarifs?upgrade=true')}
                    className={`text-${theme.primary} hover:underline font-medium`}
                  >
                    Voir les formules
                  </button>
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className={`bg-white rounded-xl shadow-sm p-6 border-t-4 border-${theme.primary}`}>
                <div className="flex items-center justify-between mb-4">
                  <BookOpen className={`h-12 w-12 text-${theme.text}`} />
                  <span className="text-3xl font-bold">{typeof globalProgress === 'number' ? globalProgress : 0}%</span>
                </div>
                <h3 className="text-lg font-semibold mb-1">Progression globale</h3>
                <p className="text-gray-600">Moyenne de progression sur tous les modules</p>
              </div>

              <div className={`bg-white rounded-xl shadow-sm p-6 border-t-4 border-${theme.primary}`}>
                <div className="flex items-center justify-between mb-4">
                  <Trophy className={`h-12 w-12 text-${theme.text}`} />
                  <span className="text-3xl font-bold">{lastQuizScore}%</span>
                </div>
                <h3 className="text-lg font-semibold mb-1">Dernier QCM</h3>
                <p className="text-gray-600">Score de votre dernière évaluation</p>
              </div>

              <div className={`bg-white rounded-xl shadow-sm p-6 border-t-4 border-${theme.primary}`}>
                <div className="flex items-center justify-between mb-4">
                  <Award className={`h-12 w-12 text-${theme.text}`} />
                  <span className="text-3xl font-bold">{bestQuizScore}%</span>
                </div>
                <h3 className="text-lg font-semibold mb-1">Meilleur score</h3>
                <p className="text-gray-600">Votre meilleure performance aux QCM</p>
              </div>
            </div>
          </>
        )}

        {/* Delete Account Section */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Supprimer le compte
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">Supprimer le compte ?</h3>
              <p className="text-gray-600 text-center mb-6">
                Attention cette action va supprimer définitivement toutes vos données. Vous n'aurez plus accès à votre progression même si vous vous réabonnez.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unsubscribe Confirmation Modal */}
        {showUnsubscribeConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">Annuler l'abonnement ?</h3>
              <p className="text-gray-600 text-center mb-6">
                Votre abonnement sera annulé à la fin de la période en cours. Vous conserverez l'accès jusqu'au {formatDate(user.subscribed_until)}.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowUnsubscribeConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Confirmer la résiliation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Level Help Modal */}
        {showLevelHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6">
              <h3 className="text-xl font-bold mb-4">Niveaux</h3>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Gagnez de l'expérience en complétant des cours et des QCM pour progresser à travers différents niveaux.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium">Comment gagner des points ?</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-2 text-gray-400" />
                      <span>5 points pour chaque cours terminé</span>
                    </li>
                    <li className="flex items-center">
                      <Brain className="h-4 w-4 mr-2 text-gray-400" />
                      <span>5 points pour un QCM non parfait</span>
                    </li>
                    <li className="flex items-center">
                      <Trophy className="h-4 w-4 mr-2 text-gray-400" />
                      <span>10 points pour un QCM à 100%</span>
                    </li>
                  </ul>
                </div>
                <div className="space-y-3">
                  {LEVELS.map((level, index) => (
                    <div key={level.name} className="flex items-center">
                      <img
                        src={level.icon}
                        alt={level.name}
                        className="w-6 h-6 object-contain mr-3"
                      />
                      <div>
                        <span className="font-medium">{level.name}</span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({level.minExperience} XP
                          {index < LEVELS.length - 1
                            ? ` - ${LEVELS[index + 1].minExperience - 1} XP`
                            : ' et plus'
                          })
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Continuez à apprendre et à vous évaluer pour atteindre le niveau suivant !
                </p>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowLevelHelp(false)}
                  className={`px-4 py-2 bg-${theme.primary} text-white rounded-lg hover:opacity-90 transition-opacity`}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}